"use client";

import { c as _c } from "react/compiler-runtime";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { TextHelper } from "@sticker-giant/sg-frontend-common";
import { IconArrowUp } from "@tabler/icons-react";
import type { Entry } from "contentful";
import { BlogBreadcrumb } from "@/components/custom/Blog/BlogBreadcrumb";
import { BlogEmptyState } from "@/components/custom/Blog/BlogEmptyState";
import { BlogLoadingSkeleton } from "@/components/custom/Blog/BlogLoadingSkeleton";
import { BlogPostGrid } from "@/components/custom/Blog/BlogPostGrid";
import { CategoryCard } from "@/components/custom/Blog/CategoryCard";
import { InfiniteScroll } from "@/components/custom/InfiniteScroll/InfiniteScroll";
import PageHeader from "@/components/custom/PageHeader";
import { Button } from "@/components/ui/button";
import { SITE_URL } from "@/constants/siteUrl";
import { getCategoryUrl } from "@/lib/contentful/categoryUrl";
import { isEntry } from "@/lib/contentful/guards";
import type { TypeBlogArticleSkeleton, TypeBlogCategorySkeleton, TypePageSkeleton } from "@/types/contentful/generated";
import { useBlogPagination, useScrollToTop, useUrlPageSync } from "./useBlogPagination";
interface Props {
  rootPage: Entry<TypePageSkeleton, undefined, string>;
  categories: Entry<TypeBlogCategorySkeleton, undefined, string>[];
  initialPosts: Entry<TypeBlogArticleSkeleton, undefined, string>[];
  initialPage: number;
  resumeFromPage: number;
  totalPages: number;
  category?: Entry<TypeBlogCategorySkeleton, undefined, string>;
  categoryIds?: string;
  draftMode?: boolean;
}
export default function BlogClient(t0) {
  const $ = _c(52);
  const {
    rootPage,
    categories,
    initialPosts,
    initialPage,
    resumeFromPage,
    totalPages,
    category,
    categoryIds,
    draftMode
  } = t0;
  const liveRootPage = useContentfulLiveUpdates(rootPage);
  const pageHeader = liveRootPage.fields.pageHeader;
  const t1 = category?.sys.id;
  let t2;
  if ($[0] !== categoryIds || $[1] !== initialPage || $[2] !== initialPosts || $[3] !== resumeFromPage || $[4] !== t1 || $[5] !== totalPages) {
    t2 = {
      initialPosts,
      initialPage,
      resumeFromPage,
      totalPages,
      categoryId: t1,
      categoryIds
    };
    $[0] = categoryIds;
    $[1] = initialPage;
    $[2] = initialPosts;
    $[3] = resumeFromPage;
    $[4] = t1;
    $[5] = totalPages;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    posts,
    postPages,
    isLoading,
    hasMore,
    loadMore
  } = useBlogPagination(t2);
  const {
    setSentinelRef
  } = useUrlPageSync(resumeFromPage);
  const {
    showScrollTop,
    scrollToTop
  } = useScrollToTop();
  let t3;
  if ($[7] !== pageHeader) {
    t3 = isEntry(pageHeader) ? pageHeader : undefined;
    $[7] = pageHeader;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const resolvedHeader = t3;
  const t4 = category?.fields.name;
  let t5;
  if ($[9] !== category) {
    t5 = category ? `${SITE_URL}${getCategoryUrl(category)}` : undefined;
    $[9] = category;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <BlogBreadcrumb currentPage={t4} url={t5} />;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = category?.fields.name;
  let t8;
  if ($[14] !== resolvedHeader || $[15] !== t7) {
    t8 = <PageHeader pageHeader={resolvedHeader} category={t7} />;
    $[14] = resolvedHeader;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== categories || $[18] !== draftMode) {
    t9 = <CategoryCard categories={categories} draftMode={draftMode} />;
    $[17] = categories;
    $[18] = draftMode;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isLoading || $[21] !== postPages || $[22] !== posts.length || $[23] !== setSentinelRef) {
    t10 = posts.length > 0 ? <BlogPostGrid postPages={postPages} setSentinelRef={setSentinelRef} /> : <BlogEmptyState isLoading={isLoading} />;
    $[20] = isLoading;
    $[21] = postPages;
    $[22] = posts.length;
    $[23] = setSentinelRef;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isLoading) {
    t11 = isLoading && <BlogLoadingSkeleton />;
    $[25] = isLoading;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== hasMore || $[28] !== isLoading || $[29] !== loadMore) {
    t12 = (hasMore || isLoading) && <div className="flex justify-center pb-8"><InfiniteScroll hasMore={hasMore} onIntersect={loadMore} /></div>;
    $[27] = hasMore;
    $[28] = isLoading;
    $[29] = loadMore;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== hasMore || $[32] !== posts.length) {
    t13 = !hasMore && posts.length > 0 && <p className="text-center text-sm text-muted-foreground pb-8">Showing all {posts.length} {TextHelper.pluralize(posts.length, "post", "posts")}.</p>;
    $[31] = hasMore;
    $[32] = posts.length;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== scrollToTop || $[35] !== showScrollTop) {
    t14 = showScrollTop && <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300"><Button variant="outline" size="icon" onClick={scrollToTop} aria-label="Back to top" className="rounded-full shadow-lg"><IconArrowUp size={18} /></Button></div>;
    $[34] = scrollToTop;
    $[35] = showScrollTop;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t14) {
    t15 = <div className="flex-1">{t10}{t11}{t12}{t13}{t14}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15 || $[44] !== t9) {
    t16 = <div className="flex flex-col lg:flex-row gap-8 px-6 pt-6 max-w-site-content mx-auto w-full">{t9}{t15}</div>;
    $[43] = t15;
    $[44] = t9;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t16 || $[47] !== t8) {
    t17 = <div className="flex flex-col">{t8}{t16}</div>;
    $[46] = t16;
    $[47] = t8;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t17 || $[50] !== t6) {
    t18 = <>{t6}{t17}</>;
    $[49] = t17;
    $[50] = t6;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}